import React, { useMemo } from 'react'
import { IQuestionViewProps } from '../types'
import { EmoticonsRatingQuestionModelTypes } from '@aytm/app/swidget/base/questions/EmoticonsRating/types'
import { ISmileysConfig } from '@aytm/lib/rfd-ui/components/swidget/types'

import ImageAsset from '../../modules/images/ImageAsset'
import TextWithOptions from '../../modules/preview/components/TextWithOptions'
import { hapticEffects } from '../../lib/hapticEffects'

import useViewport from '../../modules/viewport/useViewport'
import useSurveyLocale from '../../modules/locale/useSurveyLocale'
import usePreviewOptions from '../../modules/preview/usePreviewOptions'
import useSession from '../../session/useSession'

import Smileys from '@aytm/lib/rfd-ui/components/swidget/smileys'

import style from './style.module.scss'
import commonStyle from '../../questions/_common/style.module.scss'

export const DEFAULT_SMILEYS_SWIDGET_CONFIG: ISmileysConfig = {
  2: [
    {
      value: 0,
      icon: 'icon-aytmfd-sad-emoticon-stroked',
      iconSelected: 'icon-aytmfd-sad-emoticon-stroked-selected',
      emote: 'sad',
      label: 'sad smiley'
    },
    {
      value: 1,
      icon: 'icon-aytmfd-happy-emoticon-stroked',
      iconSelected: 'icon-aytmfd-happy-emoticon-stroked-selected',
      emote: 'happy',
      label: 'happy smiley'
    }
  ],
  3: [
    {
      value: 0,
      icon: 'icon-aytmfd-sad-emoticon-stroked',
      iconSelected: 'icon-aytmfd-sad-emoticon-stroked-selected',
      emote: 'sad',
      label: 'sad smiley'
    },
    {
      value: 1,
      icon: 'icon-aytmfd-indifferent-emoticon-stroked',
      iconSelected: 'icon-aytmfd-indifferent-emoticon-stroked-selected',
      emote: 'indifferent',
      label: 'indifferent smiley'
    },
    {
      value: 2,
      icon: 'icon-aytmfd-happy-emoticon-stroked',
      iconSelected: 'icon-aytmfd-happy-emoticon-stroked-selected',
      emote: 'happy',
      label: 'happy smiley'
    }
  ],
  5: [
    {
      value: 0,
      icon: 'icon-aytmfd-angry-emoticon-stroked',
      iconSelected: 'icon-aytmfd-angry-emoticon-stroked-selected',
      emote: 'angry',
      label: 'angry smiley'
    },
    {
      value: 1,
      icon: 'icon-aytmfd-sad-emoticon-stroked',
      iconSelected: 'icon-aytmfd-sad-emoticon-stroked-selected',
      emote: 'sad',
      label: 'sad smiley'
    },
    {
      value: 2,
      icon: 'icon-aytmfd-indifferent-emoticon-stroked',
      iconSelected: 'icon-aytmfd-indifferent-emoticon-stroked-selected',
      emote: 'indifferent',
      label: 'indifferent smiley'
    },
    {
      value: 3,
      icon: 'icon-aytmfd-happy-emoticon-stroked',
      iconSelected: 'icon-aytmfd-happy-emoticon-stroked-selected',
      emote: 'happy',
      label: 'happy smiley'
    },
    {
      value: 4,
      icon: 'icon-aytmfd-excited-emoticon-stroked',
      iconSelected: 'icon-aytmfd-excited-emoticon-stroked-selected',
      emote: 'excited',
      label: 'excited smiley'
    }
  ]
}

const EmoticonsRatingView = ({
  schema,
  response,
  onUpdateResponse,
  showValidation,
  responseValidation
}: IQuestionViewProps<EmoticonsRatingQuestionModelTypes>) => {
  const viewport = useViewport()
  const session = useSession()
  const previewOptions = usePreviewOptions()
  const { t, localeOptions } = useSurveyLocale()

  const hasSmallImages = schema.matrix_batches.filter(b => !b.hidden).some(batch => batch.image && !batch.image.expand)
  const disabled = false == session.rules?.show?.[schema.id] && !session.skipValidation
  const imageAssetWidth = viewport.emulated && viewport.width <= 420 ? 36 : 66

  const onChangeResponse = (batchIndex: number, value: number) => {
    response.data[batchIndex].value = value
    hapticEffects.selection()
    onUpdateResponse(response)
  }

  const smileysConfig = useMemo(() => {
    const defaultConfig = DEFAULT_SMILEYS_SWIDGET_CONFIG[schema.degree] || DEFAULT_SMILEYS_SWIDGET_CONFIG[3]
    return defaultConfig.map((smiley, index) =>
      smiley.emote ? { ...smiley, label: schema.matrix_batches[0]?.answers[index]?.text || smiley.label } : smiley
    )
  }, [schema.degree, schema.matrix_batches])

  return (
    <fieldset
      className={`${style.EmoticonsRatingView}
      ${viewport.mobile || (viewport.emulated && viewport.width <= 430) ? style.mobile : ''}
      ${disabled ? commonStyle.disabled : ''} ${localeOptions.rtl ? style.RTL : ''}`}
      data-testid="SW-SE"
    >
      <legend className={commonStyle.srOnly}>{schema.text}</legend>
      {!schema.matrix_batches.filter(b => !b.hidden).length ? (
        <div data-testid="SW-SE-NoAnswers">{t('questions', 'noAnswers')}</div>
      ) : null}

      {schema.matrix_batches.map((batch, index) => (
        <div
          className={`
            ${commonStyle.answerBlock}
            ${style.answerBlock}
            ${hasSmallImages ? style.compact : ''}
            ${showValidation && responseValidation.invalidBatches?.includes(batch.id) ? style.error : ''}
            ${batch.hidden ? commonStyle.hidden : ''}
            ${batch.fakeHidden ? commonStyle.fakeHidden : ''}
          `}
          key={index}
          data-testid="SW-SE-Batch"
        >
          {batch.image && batch.image?.expand && (
            <div className={style.expandWrapper} data-testid="SW-Question-ExpandedImage">
              <ImageAsset
                asset={batch.image}
                className={style.bigImage}
                width={viewport.mobile ? viewport.width : (viewport.widgetWidth as number)}
                size="medium"
                previewLabelTemplate={() => (
                  <TextWithOptions
                    text={batch.text}
                    options={previewOptions}
                    rules={session.rules?.sub?.[schema.id]?.[batch.id]}
                  />
                )}
              />
            </div>
          )}
          <div className={style.plainBlock} data-testid="SW-SE-Batch-Row">
            {batch.image && !batch.image?.expand && (
              <ImageAsset
                position={localeOptions.rtl ? 'right' : 'left'}
                asset={batch.image}
                className={localeOptions.rtl ? style.answerImageRTL : style.answerImage}
                width={imageAssetWidth}
                height={36}
                size="small"
                previewLabelTemplate={() => (
                  <TextWithOptions
                    text={batch.text}
                    options={previewOptions}
                    rules={session.rules?.sub?.[schema.id]?.[batch.id]}
                  />
                )}
              />
            )}
            {(!batch.image || batch.image.expand) && hasSmallImages ? (
              <ImageAsset width={imageAssetWidth} height={36} size="small" asset={null} className={style.answerImage} />
            ) : null}

            <fieldset className={style.answer} data-testid="SW-SE-Batch-Answer">
              {!batch.empty && (
                <legend className={style.title} data-testid="SW-SE-Batch-Text" id={`Batch-${batch.id}`}>
                  <TextWithOptions
                    text={batch.text}
                    options={previewOptions}
                    rules={session.rules?.sub?.[schema.id]?.[batch.id]}
                  />
                </legend>
              )}
              <div className={style.smilesRating} data-testid="SW-SE-Rating" aria-labelledby={`Batch-${batch.id}`}>
                <Smileys
                  smileys={smileysConfig}
                  aria-label={t('questions', 'se', 'groupLabel')}
                  onChange={value => onChangeResponse(index, value as number)}
                  selected={null == response.data[index]?.value ? null : response.data[index]?.value}
                  focusColor="#8e8e8e"
                />
                {null !== response.data[index]?.value && (
                  <div aria-live="polite" aria-atomic="true" data-testid="SW-SE-SmileText" className={style.text}>
                    <span className={commonStyle.srOnly} dangerouslySetInnerHTML={{ __html: batch.text }}></span>
                    <span dangerouslySetInnerHTML={{ __html: batch.answers[response.data[index]?.value]?.text }}></span>
                  </div>
                )}
              </div>
            </fieldset>
          </div>
        </div>
      ))}
    </fieldset>
  )
}

export default EmoticonsRatingView
